import React from 'react';
import { Phone, FileText, AlertTriangle, CheckCircle, BookOpen, Shield } from 'lucide-react';
export function DotInspectionPage() {
  return <div className="min-h-screen bg-gray-50" data-id="element-4993">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white py-16" data-id="element-4994">
        <div className="container mx-auto px-4" data-id="element-4995">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 text-center" data-id="element-4996">
            DOT Inspection
          </h1>
          <p className="text-xl text-gray-100 text-center" data-id="element-4997">
            Federal Motor Carrier Safety Regulations
          </p>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-12" data-id="element-4998">
        <div className="max-w-4xl mx-auto" data-id="element-4999">
          {/* Disclaimer */}
          <div className="bg-red-50 border-l-4 border-red-500 p-6 mb-8 text-sm" data-id="element-5000">
            <p className="text-red-900" data-id="element-5001">
              Although we make every effort to assure that the information we
              provide is complete and accurate, it is not intended to take the
              place of published agency regulations. Regulations issued by the
              U.S. Department of Transportation and its Operating
              Administrations are published in the Federal Register and compiled
              in the U.S. Code of Federal Regulations (CFR). Copies of
              appropriate volumes of the CFR in book format may be purchased
              from the Superintendent of Documents,{' '}
              <a href="http://www.gpoaccess.gov/" target="_blank" rel="noopener noreferrer" className="text-red-700 hover:underline font-semibold" data-id="element-5002">
                U.S. Government Printing Office
              </a>
              , examined at many libraries.
            </p>
          </div>

          {/* General Requirements */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5003">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5004">
              <BookOpen className="mr-3 text-[#B1050B]" size={28} data-id="element-5005" />
              General Requirements
            </h2>
            <p className="text-lg text-gray-700" data-id="element-5006">
              <strong data-id="element-5007">General —</strong> Every motor carrier, its officers,
              drivers, agents, representatives, and employees directly concerned
              with the inspection or maintenance of motor vehicles shall comply
              and be conversant with the rules of this part.
            </p>
            <p className="text-sm text-gray-600 mt-4" data-id="element-5008">
              [44 FR 38526, July 2, 1979, as amended at 53 FR 18058, May 19,
              1988]
            </p>
          </div>

          {/* §396.3 Inspection, Repair and Maintenance */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5009">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5010">
              §396.3 Inspection, Repair and Maintenance
            </h2>

            <div className="space-y-6" data-id="element-5011">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5012">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5013">(a) General</h3>
                <p className="text-gray-700 mb-3" data-id="element-5014">
                  Every motor carrier shall systematically inspect, repair, and
                  maintain, or cause to be systematically inspected, repaired,
                  and maintained, all motor vehicles subject to its control.
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-5015">
                  <li data-id="element-5016">
                    <strong data-id="element-5017">(a)(1)</strong> Parts and accessories shall be in
                    safe and proper operating condition at all times. These
                    include those specified in Part 393 and any additional parts
                    and accessories which may affect safety of operation,
                    including but not limited to, frame and frame assemblies,
                    suspension systems, axles and attaching parts, wheels and
                    rims, and steering systems.
                  </li>
                  <li data-id="element-5018">
                    <strong data-id="element-5019">(a)(2)</strong> Pushout windows, emergency doors,
                    and emergency door marking lights in buses shall be
                    inspected at least every 90 days.
                  </li>
                </ul>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-5020">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5021">
                  (b) Required Records
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-5022">
                  For vehicles controlled for 30 consecutive days or more,
                  except for a private motor carrier of passengers
                  (nonbusiness), the motor carriers shall maintain, or cause to
                  be maintained, the following record for each vehicle:
                </p>
                <ul className="space-y-2 text-gray-700 ml-4" data-id="element-5023">
                  <li data-id="element-5024">
                    <strong data-id="element-5025">(b)(1)</strong> An identification of the vehicle
                    including company number, make, serial number, year, and
                    tire size
                  </li>
                  <li data-id="element-5026">
                    <strong data-id="element-5027">(b)(2)</strong> A means to indicate the nature and
                    due date of various inspection and maintenance operations
                  </li>
                  <li data-id="element-5028">
                    <strong data-id="element-5029">(b)(3)</strong> A record of inspection, repairs and
                    maintenance indicating their date and nature
                  </li>
                  <li data-id="element-5030">
                    <strong data-id="element-5031">(b)(4)</strong> A record of tests conducted on
                    pushout windows, emergency doors, and emergency door marking
                    lights on buses
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-5032">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5033">
                  (c) Record Retention
                </h3>
                <p className="text-gray-700" data-id="element-5034">
                  The records required by this section shall be retained where
                  the vehicle is either housed or maintained for a period of 1
                  year and for 6 months after the motor vehicle leaves the motor
                  carrier's control.
                </p>
              </div>
            </div>
          </div>

          {/* §396.5 Lubrication */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5035">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5036">
              §396.5 Lubrication
            </h2>
            <p className="text-gray-700 mb-4" data-id="element-5037">
              Every motor carrier shall ensure that each motor vehicle subject
              to its control is—
            </p>
            <ul className="space-y-2 text-gray-700" data-id="element-5038">
              <li className="flex items-start" data-id="element-5039">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5040" />
                <span data-id="element-5041">
                  <strong data-id="element-5042">(a)</strong> properly lubricated; and
                </span>
              </li>
              <li className="flex items-start" data-id="element-5043">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5044" />
                <span data-id="element-5045">
                  <strong data-id="element-5046">(b)</strong> free of oil and grease leaks.
                </span>
              </li>
            </ul>
          </div>

          {/* §396.7 Unsafe Operations Forbidden */}
          <div className="bg-red-50 border-2 border-red-500 rounded-lg shadow-md p-8 mb-8" data-id="element-5047">
            <h2 className="text-2xl font-bold text-red-900 mb-6 flex items-center" data-id="element-5048">
              <AlertTriangle className="mr-3" size={28} data-id="element-5049" />
              §396.7 Unsafe Operations Forbidden
            </h2>
            <div className="space-y-4" data-id="element-5050">
              <div data-id="element-5051">
                <p className="text-gray-800 mb-2" data-id="element-5052">
                  <strong data-id="element-5053">(a) General —</strong>
                </p>
                <p className="text-gray-700" data-id="element-5054">
                  A motor vehicle shall not be operated in such a condition as
                  to likely cause an accident or a breakdown of the vehicle.
                </p>
              </div>
              <div data-id="element-5055">
                <p className="text-gray-800 mb-2" data-id="element-5056">
                  <strong data-id="element-5057">(b) Exemption —</strong>
                </p>
                <p className="text-gray-700" data-id="element-5058">
                  Any motor vehicle discovered to be in an unsafe condition
                  while being operated on the highway may be continued in
                  operation only to the nearest place where repairs can safely
                  be effected. Such operation shall be conducted only if it is
                  less hazardous to the public than to permit the vehicle to
                  remain on the highway.
                </p>
              </div>
            </div>
          </div>

          {/* §396.9 Inspection of Motor Vehicles in Operation */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5059">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5060">
              §396.9 Inspection of Motor Vehicles in Operation
            </h2>

            <div className="space-y-6" data-id="element-5061">
              <div data-id="element-5062">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5063">
                  (a) Personnel Authorized to Perform Inspections
                </h3>
                <p className="text-gray-700" data-id="element-5064">
                  Every special agent of the FMCSA is authorized to enter upon
                  and perform inspections of motor carrier's vehicles in
                  operation.
                </p>
              </div>

              <div data-id="element-5065">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5066">
                  (b) Prescribed Inspection Report
                </h3>
                <p className="text-gray-700" data-id="element-5067">
                  The Driver Vehicle Examination Report shall be used to record
                  results of motor vehicle inspections conducted by authorized
                  FMCSA personnel.
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-500 p-6" data-id="element-5068">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5069">
                  (c) Motor Vehicles Declared "Out of Service"
                </h3>
                <ul className="space-y-3 text-gray-700" data-id="element-5070">
                  <li data-id="element-5071">
                    <strong data-id="element-5072">(c)(1)</strong> Authorized personnel shall declare
                    and mark "out of service" any motor vehicle which by reason
                    of its mechanical condition or loading would likely cause an
                    accident or a breakdown.
                  </li>
                  <li data-id="element-5073">
                    <strong data-id="element-5074">(c)(2)</strong> No motor carrier shall require or
                    permit any person to operate nor shall any person operate
                    any motor vehicle declared and marked "out of service" until
                    all repairs required by the "out of service notice" have
                    been satisfactorily completed.
                  </li>
                  <li data-id="element-5075">
                    <strong data-id="element-5076">(c)(3)</strong> No person shall remove the "Out of
                    Service Vehicle" sticker from any motor vehicle prior to
                    completion of all repairs required by the "out of service
                    notice".
                  </li>
                </ul>
              </div>

              <div data-id="element-5077">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5078">
                  (d) Motor Carrier Disposition
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5079">
                  <li data-id="element-5080">
                    <strong data-id="element-5081">(d)(1)</strong> The driver shall deliver the
                    inspection report to the motor carrier upon arrival at the
                    next terminal or facility, or mail it immediately if not
                    scheduled to arrive within 24 hours.
                  </li>
                  <li data-id="element-5082">
                    <strong data-id="element-5083">(d)(2)</strong> Motor carriers shall examine the
                    report. Violations or defects noted thereon shall be
                    corrected.
                  </li>
                  <li data-id="element-5084">
                    <strong data-id="element-5085">(d)(3)</strong> Within 15 days following the date of
                    inspection, the motor carrier shall certify that all
                    violations noted have been corrected and return the
                    completed form to the issuing agency.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* §396.11 Driver Vehicle Inspection Report */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5086">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5087">
              §396.11 Driver Vehicle Inspection Report(s)
            </h2>

            <div className="space-y-6" data-id="element-5088">
              <div className="bg-blue-50 border-l-4 border-blue-500 p-6" data-id="element-5089">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5090">
                  (a) Report Required
                </h3>
                <p className="text-gray-700 mb-3" data-id="element-5091">
                  Every motor carrier shall require its drivers to report, and
                  every driver shall prepare a report in writing at the
                  completion of each day's work on each vehicle operated. The
                  report shall cover at least the following parts and
                  accessories:
                </p>
                <div className="grid md:grid-cols-2 gap-2 text-gray-700" data-id="element-5092">
                  <div className="flex items-start" data-id="element-5093">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5094" />
                    <span data-id="element-5095">
                      Service brakes including trailer brake connections
                    </span>
                  </div>
                  <div className="flex items-start" data-id="element-5096">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5097" />
                    <span data-id="element-5098">Parking (hand) brake</span>
                  </div>
                  <div className="flex items-start" data-id="element-5099">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5100" />
                    <span data-id="element-5101">Steering mechanism</span>
                  </div>
                  <div className="flex items-start" data-id="element-5102">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5103" />
                    <span data-id="element-5104">Lighting devices and reflectors</span>
                  </div>
                  <div className="flex items-start" data-id="element-5105">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5106" />
                    <span data-id="element-5107">Tires</span>
                  </div>
                  <div className="flex items-start" data-id="element-5108">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5109" />
                    <span data-id="element-5110">Horn</span>
                  </div>
                  <div className="flex items-start" data-id="element-5111">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5112" />
                    <span data-id="element-5113">Windshield wipers</span>
                  </div>
                  <div className="flex items-start" data-id="element-5114">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5115" />
                    <span data-id="element-5116">Rear vision mirrors</span>
                  </div>
                  <div className="flex items-start" data-id="element-5117">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5118" />
                    <span data-id="element-5119">Coupling devices</span>
                  </div>
                  <div className="flex items-start" data-id="element-5120">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5121" />
                    <span data-id="element-5122">Wheels and rims</span>
                  </div>
                  <div className="flex items-start" data-id="element-5123">
                    <CheckCircle className="text-blue-600 mr-2 flex-shrink-0 mt-1" size={16} data-id="element-5124" />
                    <span data-id="element-5125">Emergency equipment</span>
                  </div>
                </div>
              </div>

              <div data-id="element-5126">
                <h3 className="font-bold text-gray-900 mb-2" data-id="element-5127">
                  (b) Report Content
                </h3>
                <p className="text-gray-700" data-id="element-5128">
                  The report shall identify the vehicle and list any defect or
                  deficiency discovered. If no defect or deficiency is
                  discovered, the report shall so indicate. The driver shall
                  sign the report.
                </p>
              </div>

              <div className="bg-green-50 border-l-4 border-green-500 p-6" data-id="element-5129">
                <h3 className="font-bold text-gray-900 mb-3" data-id="element-5130">
                  (c) Corrective Action
                </h3>
                <ul className="space-y-2 text-gray-700" data-id="element-5131">
                  <li data-id="element-5132">
                    <strong data-id="element-5133">(c)(1)</strong> Every motor carrier shall certify on
                    the original driver vehicle inspection report that the
                    defect or deficiency has been repaired or that repair is
                    unnecessary before the vehicle is operated again.
                  </li>
                  <li data-id="element-5134">
                    <strong data-id="element-5135">(c)(2)</strong> Every motor carrier shall maintain
                    the original driver vehicle inspection report, the
                    certification of repairs, and the certification of the
                    driver's review for three months from the date the written
                    report was prepared.
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* §396.13 Driver Inspection */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5136">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5137">
              §396.13 Driver Inspection
            </h2>
            <p className="text-gray-700 mb-4" data-id="element-5138">
              Before driving a motor vehicle, the driver shall:
            </p>
            <ul className="space-y-2 text-gray-700" data-id="element-5139">
              <li className="flex items-start" data-id="element-5140">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5141" />
                <span data-id="element-5142">
                  <strong data-id="element-5143">(a)</strong> Be satisfied that the motor vehicle is in
                  safe operating condition;
                </span>
              </li>
              <li className="flex items-start" data-id="element-5144">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5145" />
                <span data-id="element-5146">
                  <strong data-id="element-5147">(b)</strong> Review the last driver vehicle inspection
                  report; and
                </span>
              </li>
              <li className="flex items-start" data-id="element-5148">
                <CheckCircle className="text-green-600 mr-2 flex-shrink-0 mt-1" size={18} data-id="element-5149" />
                <span data-id="element-5150">
                  <strong data-id="element-5151">(c)</strong> Sign the report, only if defects or
                  deficiencies were noted, to acknowledge review and
                  certification that required repairs have been performed.
                </span>
              </li>
            </ul>
          </div>

          {/* §396.17 Periodic Inspection */}
          <div className="bg-gradient-to-r from-[#B1050B] to-[#8A0408] text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5152">
            <h2 className="text-3xl font-bold mb-6 flex items-center" data-id="element-5153">
              <Shield className="mr-3" size={32} data-id="element-5154" />
              §396.17 Periodic Inspection
            </h2>

            <div className="space-y-4" data-id="element-5155">
              <div className="bg-white/10 rounded-lg p-4" data-id="element-5156">
                <p className="mb-2" data-id="element-5157">
                  <strong data-id="element-5158">(a)</strong> Every commercial motor vehicle shall be
                  inspected as required by this section. The inspection shall
                  include, at a minimum, the parts and accessories set forth in
                  Appendix G.
                </p>
                <p className="text-sm text-gray-200" data-id="element-5159">
                  Note: The term commercial motor vehicle includes each vehicle
                  in a combination vehicle.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-5160">
                <p data-id="element-5161">
                  <strong data-id="element-5162">(b)</strong> A motor carrier shall inspect or cause to
                  be inspected all motor vehicles subject to its control.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-5163">
                <p className="mb-2" data-id="element-5164">
                  <strong data-id="element-5165">(c)</strong> A motor carrier shall not use a
                  commercial motor vehicle unless each component has passed an
                  inspection at least once during the preceding 12 months and
                  documentation is on the vehicle.
                </p>
              </div>

              <div className="bg-white/10 rounded-lg p-4" data-id="element-5166">
                <p data-id="element-5167">
                  <strong data-id="element-5168">(f)</strong> Vehicles passing roadside or periodic
                  inspections performed under State or FMCSA auspices will be
                  considered to have met annual inspection requirements for 12
                  months.
                </p>
              </div>
            </div>
          </div>

          {/* §396.19 Inspector Qualifications */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5169">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5170">
              §396.19 Inspector Qualifications
            </h2>

            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-4" data-id="element-5171">
              <p className="text-gray-700 mb-3" data-id="element-5172">
                The motor carrier shall ensure that individuals performing
                annual inspections are qualified as follows:
              </p>
              <ul className="space-y-2 text-gray-700" data-id="element-5173">
                <li data-id="element-5174">
                  <strong data-id="element-5175">(a)(1)</strong> Understands the inspection criteria
                  set forth in 49 CFR Part 393 and Appendix G and can identify
                  defective components
                </li>
                <li data-id="element-5176">
                  <strong data-id="element-5177">(a)(2)</strong> Is knowledgeable of and has mastered
                  the methods, procedures, tools and equipment used when
                  performing an inspection
                </li>
                <li data-id="element-5178">
                  <strong data-id="element-5179">(a)(3)</strong> Is capable of performing an inspection
                  by reason of experience, training, or both
                </li>
              </ul>
            </div>

            <div className="bg-green-50 rounded-lg p-6" data-id="element-5180">
              <h3 className="font-bold text-gray-900 mb-3" data-id="element-5181">
                Qualifying Training/Experience:
              </h3>
              <ul className="space-y-2 text-gray-700" data-id="element-5182">
                <li data-id="element-5183">
                  • Successfully completed State or Federal sponsored training
                  program, OR
                </li>
                <li data-id="element-5184">• Has certificate from State or Canadian Province, OR</li>
                <li data-id="element-5185">
                  • Has combination of training and/or experience totaling at
                  least 1 year
                </li>
              </ul>
            </div>
          </div>

          {/* §396.25 Brake Inspector Qualifications */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5186">
            <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-5187">
              §396.25 Qualifications of Brake Inspectors
            </h2>

            <div className="space-y-4" data-id="element-5188">
              <p className="text-gray-700" data-id="element-5189">
                <strong data-id="element-5190">(a)</strong> The motor carrier shall ensure that all
                inspections, maintenance, repairs or service to the brakes of
                its commercial motor vehicles are performed in compliance with
                the requirements of this section.
              </p>

              <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6" data-id="element-5191">
                <p className="text-gray-800 mb-3" data-id="element-5192">
                  <strong data-id="element-5193">(d)</strong> The motor carrier shall ensure that each
                  brake inspector is qualified as follows:
                </p>
                <ul className="space-y-2 text-gray-700" data-id="element-5194">
                  <li data-id="element-5195">
                    <strong data-id="element-5196">(d)(1)</strong> Understands the brake service or
                    inspection task and can perform it
                  </li>
                  <li data-id="element-5197">
                    <strong data-id="element-5198">(d)(2)</strong> Is knowledgeable of methods,
                    procedures, tools and equipment
                  </li>
                  <li data-id="element-5199">
                    <strong data-id="element-5200">(d)(3)</strong> Is capable by reason of experience,
                    training or both (at least 1 year combined)
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* DOT Inspection Form Sample */}
          <div className="bg-white rounded-lg shadow-md p-8 mb-8" data-id="element-5201">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center" data-id="element-5202">
              <FileText className="mr-3 text-[#B1050B]" size={28} data-id="element-5203" />
              DOT Inspection Form Sample
            </h2>
            <div className="border-2 border-gray-300 rounded-lg overflow-hidden" data-id="element-5204">
              <img src="//drwzpk38qkpfb.cloudfront.net/www.garrettauto.com/preview/uploaded/images/DOT%20INSPECTION%20FORM%20sample.JPG?opt=w3" alt="DOT Inspection Form Sample" className="w-full h-auto" data-id="element-5205" />
            </div>
          </div>

          {/* Key Takeaways */}
          <div className="bg-gradient-to-r from-green-600 to-green-700 text-white rounded-lg shadow-xl p-8 mb-8" data-id="element-5206">
            <h2 className="text-3xl font-bold mb-6" data-id="element-5207">Key Takeaways</h2>
            <div className="space-y-3" data-id="element-5208">
              <div className="flex items-start" data-id="element-5209">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5210" />
                <span data-id="element-5211">
                  Annual inspections required for all commercial motor vehicles
                </span>
              </div>
              <div className="flex items-start" data-id="element-5212">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5213" />
                <span data-id="element-5214">Daily driver vehicle inspection reports mandatory</span>
              </div>
              <div className="flex items-start" data-id="element-5215">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5216" />
                <span data-id="element-5217">
                  Qualified inspectors must meet specific training/experience
                  requirements
                </span>
              </div>
              <div className="flex items-start" data-id="element-5218">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5219" />
                <span data-id="element-5220">Records must be maintained for specified periods</span>
              </div>
              <div className="flex items-start" data-id="element-5221">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5222" />
                <span data-id="element-5223">
                  Unsafe vehicles cannot be operated until repairs are completed
                </span>
              </div>
              <div className="flex items-start" data-id="element-5224">
                <CheckCircle className="mr-3 flex-shrink-0 mt-1" size={20} data-id="element-5225" />
                <span data-id="element-5226">Brake inspectors require specialized qualifications</span>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg shadow-xl p-8 text-center" data-id="element-5227">
            <h2 className="text-3xl font-bold mb-4" data-id="element-5228">
              Need DOT Inspection Services?
            </h2>
            <p className="text-xl mb-6" data-id="element-5229">
              Certified inspectors ensuring compliance with all federal
              regulations
            </p>
            <a href="tel:2484788382" className="inline-flex items-center bg-[#B1050B] hover:bg-[#8A0408] text-white px-8 py-4 rounded-lg font-bold text-lg transition-colors shadow-lg" data-id="element-5230">
              <Phone className="mr-3" size={24} data-id="element-5231" />
              Call (248) 478-8382
            </a>
            <p className="mt-4 text-gray-300" data-id="element-5232">
              24175 Industrial Park Dr., Farmington Hills, MI 48335
            </p>
          </div>
        </div>
      </div>
    </div>;
}